import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AddInventoryDialog from '../components/AddInventoryDialog';
import EditInventoryDialog from '../components/EditInventoryDialog';
import InventoryAuditDialog from '../components/InventoryAuditDialog';
import CsvMenu from '../components/CsvMenu';
import PartsOrderLinkBanner from '../components/PartsOrderLinkBanner';
import HeaderMenu from '../components/HeaderMenu';
import SerializedComponentsList from '../components/SerializedComponentsList';
import PMPackagesList from '../components/PMPackagesList';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';

const StockAlertRow = ({ item, severity, isAdmin, onUpdateStock }) => {
  const tone =
    severity === 'out'
      ? 'bg-red-50/60 border-red-200 hover:bg-red-50'
      : 'bg-amber-50/60 border-amber-200 hover:bg-amber-50';
  const badgeTone =
    severity === 'out'
      ? 'bg-red-500 hover:bg-red-600'
      : 'bg-amber-500 hover:bg-amber-600';
  return (
    <div
      className={`flex items-center justify-between gap-3 p-3 rounded-md border ${tone} transition`}
      data-testid={`low-stock-row-${item.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-sm font-semibold">{item.part_number}</span>
          <Badge className={`${badgeTone} text-xs`}>
            {severity === 'out' ? 'Out of Stock' : 'Low Stock'}
          </Badge>
        </div>
        <div className="text-sm text-slate-700 mt-0.5 truncate">{item.name}</div>
      </div>
      <div className="flex flex-col items-end shrink-0 text-right">
        <span className="text-sm font-semibold">
          {item.quantity} {item.unit}
          {item.serialized_installable > 0 && (
            <span className="text-blue-700 font-normal text-xs ml-1">
              + {item.serialized_installable} SN
            </span>
          )}
          {item.reorder_level != null && (
            <span className="text-slate-500 font-normal text-xs ml-1">
              / reorder @ {item.reorder_level}
            </span>
          )}
        </span>
        {isAdmin && (
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={() => onUpdateStock(item)}
            data-testid={`low-stock-update-btn-${item.id}`}
          >
            Update stock →
          </Button>
        )}
      </div>
    </div>
  );
};

const Inventory = ({ user, onLogout }) => {
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [equipFilterIds, setEquipFilterIds] = useState([]);
  const [showEquipFilter, setShowEquipFilter] = useState(false);
  const [machinery, setMachinery] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alertsCollapsed, setAlertsCollapsed] = useState(() => {
    try { return localStorage.getItem('inventoryAlertsCollapsed') === '1'; } catch { return false; }
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isAdmin = user.role === 'admin' || user.role === 'technician';

  // Tab switcher: "consumables" (existing bulk-inventory workflow) vs
  // "serialized" (uniquely-tracked wear parts). Tab persists via ?tab=... so
  // deep links from the equipment page land users on the right list.
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get('tab');
    return (t === 'serialized' || t === 'pm-packages') ? t : 'consumables';
  });
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t === 'serialized' || t === 'consumables' || t === 'pm-packages') setActiveTab(t);
  }, [searchParams]);

  const toggleAlerts = () => {
    setAlertsCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem('inventoryAlertsCollapsed', next ? '1' : '0'); } catch { /* noop */ }
      return next;
    });
  };

  useEffect(() => {
    const refresh = () => fetchInventory();
    window.addEventListener('inventory-changed', refresh);
    return () => window.removeEventListener('inventory-changed', refresh);
  }, []);

  useEffect(() => {
    fetchInventory();
    (async () => {
      try {
        const [locRes, mRes] = await Promise.all([
          axios.get(`${API}/locations`),
          axios.get(`${API}/machinery`),
        ]);
        setLocations(locRes.data || []);
        setMachinery(mRes.data || []);
      } catch { /* non-fatal */ }
    })();
  }, []);

  // Deep-link support: /inventory?applicable=<machineryId> prefills the filter.
  useEffect(() => {
    const apId = searchParams.get('applicable');
    if (apId && !equipFilterIds.includes(apId)) {
      setEquipFilterIds([apId]);
      setShowEquipFilter(true);
    }
    // Intentionally not depending on equipFilterIds — one-shot on URL change.
  }, [searchParams]);

  useEffect(() => {
    let result = inventory;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter(
        (item) =>
          [item.part_number, item.name, item.description, item.supplier]
            .some((value) => (value || '').toLowerCase().includes(q))
      );
    }
    if (equipFilterIds.length > 0) {
      const equipSet = new Set(equipFilterIds);
      // Union filter: show parts applicable to ANY of the selected equipment
      result = result.filter((it) =>
        (it.applicable_machinery_ids || []).some((id) => equipSet.has(id))
      );
    }
    setFilteredInventory(result);
  }, [searchTerm, inventory, equipFilterIds]);

  const fetchInventory = async () => {
    try {
      const response = await axios.get(`${API}/inventory`);
      setInventory(response.data);
      setFilteredInventory(response.data);
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setShowEditDialog(true);
  };

  const handleShowHistory = (item) => {
    setSelectedItem(item);
    setShowAuditDialog(true);
  };

  const handleDelete = async (itemId) => {
    try {
      await axios.delete(`${API}/inventory/${itemId}`);
      toast.success('Inventory item deleted');
      fetchInventory();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const toggleTrack = async (item) => {
    // Optimistic UI — flip the star instantly, roll back on error.
    const next = !item.is_tracked;
    setInventory((prev) => prev.map((it) => (it.id === item.id ? { ...it, is_tracked: next } : it)));
    try {
      await axios.patch(`${API}/inventory/${item.id}/track`, { is_tracked: next });
      toast.success(
        next
          ? `Now tracking ${item.part_number} on Analytics`
          : `Stopped tracking ${item.part_number}`
      );
    } catch (e) {
      setInventory((prev) => prev.map((it) => (it.id === item.id ? { ...it, is_tracked: !next } : it)));
      toast.error(e.response?.data?.detail || 'Failed to toggle tracking');
    }
  };

  const getStockBadge = (item) => {
    if (item.non_critical) {
      return <Badge className="bg-slate-400">Non-critical</Badge>;
    }
    // Effective stock counts bulk + New/Available serialized units in the same
    // MFR PN family. This is what determines whether reorder alerts fire.
    const effective = (item.quantity || 0) + (item.serialized_installable || 0);
    if (effective === 0) {
      return <Badge className="bg-red-500">Out of Stock</Badge>;
    } else if (item.reorder_level && effective <= item.reorder_level) {
      return <Badge className="bg-yellow-500">Low Stock</Badge>;
    } else {
      return <Badge className="bg-green-500">In Stock</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Inventory Management</h1>
              <p className="text-sm text-slate-600 mt-1">
                Track parts and supplies
              </p>
            </div>
            <div className="flex gap-2">
              <HeaderMenu user={user} onLogout={onLogout} />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab switcher: consumables (bulk parts) vs serialized (unique parts). */}
        <div className="flex gap-2 mb-4" data-testid="inventory-tabs">
          {[
            { v: 'consumables', label: 'Bulk Inventory' },
            { v: 'serialized', label: 'Serialized Parts' },
            { v: 'pm-packages', label: 'PM Packages' },
          ].map((t) => (
            <button
              key={t.v}
              type="button"
              onClick={() => {
                setActiveTab(t.v);
                const usp = new URLSearchParams(window.location.search);
                if (t.v === 'consumables') usp.delete('tab');
                else usp.set('tab', t.v);
                navigate(`/inventory${usp.toString() ? '?' + usp.toString() : ''}`, { replace: true });
              }}
              className={
                'px-4 py-2 rounded-md text-sm font-medium transition border ' +
                (activeTab === t.v
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400')
              }
              data-testid={`inventory-tab-${t.v}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'serialized' ? (
          <SerializedComponentsList
            isAdmin={isAdmin}
            initialQuery={searchParams.get('q') || ''}
          />
        ) : activeTab === 'pm-packages' ? (
          <PMPackagesList />
        ) : (
          <>
        {/* Parts-order spreadsheet URL (everyone sees, admins/techs edit) */}
        <PartsOrderLinkBanner isAdmin={isAdmin} />

        {/* Stock Alerts — auto-hides when nothing is below reorder.
            Non-critical items (supplier-managed / perpetual-zero) are
            deliberately excluded here so they don't clutter the alert list.
            Effective stock = bulk quantity + New/Available serialized units
            in the same MFR PN family. This keeps families that have moved
            to serialized tracking from spamming false out-of-stock alerts. */}
        {(() => {
          const effectiveQty = (i) => (i.quantity || 0) + (i.serialized_installable || 0);
          const outOfStock = inventory.filter((i) => !i.non_critical && effectiveQty(i) === 0);
          const lowStock = inventory.filter(
            (i) => !i.non_critical && effectiveQty(i) > 0 && i.reorder_level != null && effectiveQty(i) <= i.reorder_level
          );
          const totalAlerts = outOfStock.length + lowStock.length;
          if (totalAlerts === 0) return null;

          return (
            <Card className="mb-6 border-amber-200" data-testid="low-stock-alerts-card">
              <CardHeader className="pb-3">
                <button
                  type="button"
                  onClick={toggleAlerts}
                  className="w-full flex items-center justify-between text-base font-semibold text-left"
                  data-testid="stock-alerts-toggle"
                  aria-expanded={!alertsCollapsed}
                >
                  <span className="flex items-center gap-2">
                    <span className="text-slate-400">{alertsCollapsed ? '▸' : '▾'}</span>
                    Stock Alerts
                  </span>
                  <span className="text-sm font-normal text-slate-500">
                    {totalAlerts} part{totalAlerts === 1 ? '' : 's'} need
                    {totalAlerts === 1 ? 's' : ''} attention
                  </span>
                </button>
              </CardHeader>
              {!alertsCollapsed && (
                <CardContent data-testid="stock-alerts-body">
                  <div className="space-y-2">
                  {outOfStock.map((item) => (
                    <StockAlertRow
                      key={item.id}
                      item={item}
                      severity="out"
                      isAdmin={isAdmin}
                      onUpdateStock={handleEdit}
                    />
                  ))}
                  {lowStock.map((item) => (
                    <StockAlertRow
                      key={item.id}
                      item={item}
                      severity="low"
                      isAdmin={isAdmin}
                      onUpdateStock={handleEdit}
                    />
                  ))}
                </div>
              </CardContent>
              )}
            </Card>
          );
        })()}

        {/* Search and Actions */}
        <div className="bg-white rounded-lg shadow p-4 mb-6 space-y-3">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                data-testid="search-inventory-input"
                placeholder="Search part number, name, description or supplier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowEquipFilter((v) => !v)}
              data-testid="equip-filter-toggle"
            >
              Applies to equipment {equipFilterIds.length > 0 ? `(${equipFilterIds.length})` : '▾'}
            </Button>
            {isAdmin && (
              <Button onClick={() => setShowAddDialog(true)} data-testid="add-inventory-btn">
                Add Item
              </Button>
            )}
            <CsvMenu
              exportPath="/inventory/export.csv"
              templatePath="/inventory/import-template.csv"
              importPath="/inventory/import"
              kind="inventory"
              dialogTitle="Import Inventory CSV"
              dialogDescription="Match by part_number — existing rows are updated, new rows are created. Blank cells preserve the current value."
              onImported={fetchInventory}
              canImport={isAdmin}
              testIdPrefix="inv"
            />
          </div>

          {showEquipFilter && (
            <div className="border-t pt-3" data-testid="equip-filter-panel">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">
                  Filter parts by applicable equipment
                  <span className="text-xs text-slate-500 ml-2">
                    (multi-select — shows parts that fit ANY of the selected)
                  </span>
                </span>
                {equipFilterIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setEquipFilterIds([])}
                    className="text-xs text-red-600 hover:underline"
                    data-testid="equip-filter-clear"
                  >
                    Clear all
                  </button>
                )}
              </div>
              {(() => {
                const byLoc = new Map();
                const unassignedList = [];
                machinery.forEach((m) => {
                  if (m.plant_location_id) {
                    if (!byLoc.has(m.plant_location_id)) byLoc.set(m.plant_location_id, []);
                    byLoc.get(m.plant_location_id).push(m);
                  } else {
                    unassignedList.push(m);
                  }
                });
                // Sort each bucket alphabetically by component
                byLoc.forEach((arr) => arr.sort((a, b) => (a.component || '').localeCompare(b.component || '')));
                unassignedList.sort((a, b) => (a.component || '').localeCompare(b.component || ''));
                const sortedLocs = [...locations].sort((a, b) => a.name.localeCompare(b.name));

                const renderChip = (m) => {
                  const on = equipFilterIds.includes(m.id);
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() =>
                        setEquipFilterIds((prev) =>
                          on ? prev.filter((x) => x !== m.id) : [...prev, m.id]
                        )
                      }
                      data-testid={`equip-filter-chip-${m.id}`}
                      className={`text-xs px-2.5 py-1 rounded-full border transition ${
                        on
                          ? 'bg-slate-900 text-white border-slate-900'
                          : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
                      }`}
                    >
                      {m.component}
                    </button>
                  );
                };

                return (
                  <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                    {sortedLocs.map((loc) => {
                      const items = byLoc.get(loc.id) || [];
                      if (items.length === 0) return null;
                      return (
                        <div key={loc.id}>
                          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">
                            {loc.name}
                          </div>
                          <div className="flex flex-wrap gap-1.5">{items.map(renderChip)}</div>
                        </div>
                      );
                    })}
                    {unassignedList.length > 0 && (
                      <div>
                        <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">
                          Unassigned Equipment
                        </div>
                        <div className="flex flex-wrap gap-1.5">{unassignedList.map(renderChip)}</div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* Inventory Table */}
        <Card>
          <CardHeader>
            <CardTitle>Inventory Items ({filteredInventory.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredInventory.length === 0 ? (
              <div className="text-center py-12 text-slate-600">
                No inventory items found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b">
                    <tr className="text-left text-sm text-slate-600">
                      <th className="pb-3 font-semibold w-8" title="Track on Analytics">★</th>
                      <th className="pb-3 font-semibold">Part Number</th>
                      <th className="pb-3 font-semibold">Name</th>
                      <th className="pb-3 font-semibold">Quantity</th>
                      <th className="pb-3 font-semibold">Status</th>
                      <th className="pb-3 font-semibold">Location</th>
                      <th className="pb-3 font-semibold">Reorder At</th>
                      <th className="pb-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInventory.map((item) => (
                      <tr
                        key={item.id}
                        className="border-b hover:bg-slate-50"
                        data-testid={`inventory-row-${item.id}`}
                      >
                        <td className="py-3">
                          {isAdmin ? (
                            <button
                              type="button"
                              onClick={() => toggleTrack(item)}
                              data-testid={`track-btn-${item.id}`}
                              title={item.is_tracked ? 'Tracked on Analytics — click to untrack' : 'Track this part on the Analytics page'}
                              className={`text-lg leading-none transition ${
                                item.is_tracked ? 'text-amber-500 hover:text-amber-600' : 'text-slate-300 hover:text-amber-400'
                              }`}
                              aria-label={item.is_tracked ? 'Untrack part' : 'Track part'}
                            >
                              {item.is_tracked ? '★' : '☆'}
                            </button>
                          ) : (
                            <span
                              className={`text-lg ${item.is_tracked ? 'text-amber-500' : 'text-slate-300'}`}
                              title={item.is_tracked ? 'Tracked on Analytics' : 'Not tracked'}
                            >
                              {item.is_tracked ? '★' : '☆'}
                            </span>
                          )}
                        </td>
                        <td className="py-3 font-mono text-sm">{item.part_number}</td>
                        <td className="py-3">{item.name}</td>
                        <td className="py-3">
                          <div className="font-semibold whitespace-nowrap">
                            {item.quantity} {item.unit}
                          </div>
                          {item.serialized_total > 0 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveTab('serialized');
                                const usp = new URLSearchParams();
                                usp.set('tab', 'serialized');
                                usp.set('q', item.part_number);
                                navigate(`/inventory?${usp.toString()}`, { replace: true });
                              }}
                              className="mt-1 inline-flex items-center gap-1 text-xs bg-blue-50 hover:bg-blue-100 text-blue-800 rounded px-1.5 py-0.5 transition"
                              title={`View ${item.serialized_total} serialized parts with this MFR PN`}
                              data-testid={`inv-serialized-chip-${item.id}`}
                            >
                              + {item.serialized_installable} Serialized
                              <span className="text-blue-600 text-[10px]">
                                ({item.serialized_installed} installed)
                              </span>
                            </button>
                          )}
                        </td>
                        <td className="py-3">{getStockBadge(item)}</td>
                        <td className="py-3 text-slate-600">{item.location || '-'}</td>
                        <td className="py-3 text-slate-600">
                          {item.reorder_level ? `${item.reorder_level} ${item.unit}` : '-'}
                        </td>
                        <td className="py-3">
                          <div className="flex gap-2 flex-wrap">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleShowHistory(item)}
                              data-testid={`history-inventory-btn-${item.id}`}
                            >
                              History
                            </Button>
                            {isAdmin && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEdit(item)}
                                  data-testid={`edit-inventory-btn-${item.id}`}
                                >
                                  Edit
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      data-testid={`delete-inventory-btn-${item.id}`}
                                    >
                                      Delete
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Delete Inventory Item?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This will permanently delete {item.name} (Part: {item.part_number}) from inventory.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => handleDelete(item.id)}>
                                        Delete
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
          </>
        )}
      </div>

      {isAdmin && (
        <>
          <AddInventoryDialog
            open={showAddDialog}
            onClose={() => setShowAddDialog(false)}
            onSuccess={() => {
              setShowAddDialog(false);
              fetchInventory();
            }}
          />
          <EditInventoryDialog
            open={showEditDialog}
            onClose={() => {
              setShowEditDialog(false);
              setSelectedItem(null);
            }}
            item={selectedItem}
            onSuccess={() => {
              setShowEditDialog(false);
              setSelectedItem(null);
              fetchInventory();
            }}
          />
        </>
      )}
      <InventoryAuditDialog
        open={showAuditDialog}
        onClose={() => {
          setShowAuditDialog(false);
          setSelectedItem(null);
        }}
        item={showAuditDialog ? selectedItem : null}
      />
    </div>
  );
};

export default Inventory;
